import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { themeQuartz } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_useBlocker as useBlocker, useSearchParams } from 'react-router-dom';

import { listInvoiceGroupsApi } from 'api/invoiceGroup';
import {
  listInvoiceGroupProductPricingsApi,
  listProductPricingsApi,
  updateInvoiceGroupProductPricingsApi,
  updateProductPricingsApi,
} from 'api/productPricing';

import AddProductPricingDialog from './components/AddProductPricingDialog';
import GridFooterActions from './components/GridFooterActions';
import SaveErrorsAlert from './components/SaveErrorsAlert';
import { DiscardChangesDialog, LeaveWithoutSavingDialog } from './components/UnsavedChangesDialogs';
import {
  ADDED_ROW_COLOR,
  EDITABLE_FIELDS,
  EDITED_CELL_COLOR,
  EDITED_ROW_COLOR,
  ERROR_CELL_COLOR,
  ERROR_CELL_OUTLINE,
  baseColDef,
  buildColumnDefs,
  getTextAlign,
} from './utils/productPricingColumns';
import {
  VALIDATED_FIELDS,
  getCellProblem,
  mergeSavedRows,
  saveErrorKey,
  toSaveErrors,
  toSavePayload,
} from './utils/productPricingHelpers';

const NO_ADDED_ROWS = [];

const DEFAULT_PRICING_OPTION = { invoiceGroupId: '', invoiceGroupName: 'Default pricing' };

const editedCellKey = (ndc, field) => `${ndc}:${field}`;

const ProductPricing = () => {
  const theme = useTheme();

  // Nothing is editable while a save is in flight (see handleSave). Read through a ref so the
  // column defs don't change identity when saving starts and stops.
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  isSavingRef.current = isSaving;
  const defaultColDef = useMemo(
    () => ({ ...baseColDef, editable: () => !isSavingRef.current }),
    []
  );

  // Compact rows with cell borders and alternating row shading, so the grid reads like the
  // price sheet.
  const spreadsheetTheme = useMemo(
    () =>
      themeQuartz.withParams({
        columnBorder: true,
        headerColumnBorder: true,
        rowHeight: 32,
        headerHeight: 36,
        fontSize: 13,
        oddRowBackgroundColor: theme.palette.action.hover,
      }),
    [theme]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const invoiceGroupIdParam = searchParams.get('invoiceGroupId') ?? '';

  const [invoiceGroups, setInvoiceGroups] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);
  const [defaults, setDefaults] = useState([]);
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(true);
  const [isDefaultsError, setIsDefaultsError] = useState(false);
  // Overrides are stored with the group they belong to, so a group whose overrides haven't
  // arrived yet reads as loading rather than briefly showing another group's rows.
  const [loadedOverrides, setLoadedOverrides] = useState({
    groupId: '',
    results: [],
    isError: false,
  });

  // Edits live in the grid until saved. Bumping resetToken rebuilds the rows from what the API
  // returned, discarding every edit.
  const [resetToken, setResetToken] = useState(0);
  // Rows added in this session, tied to the rows they were added on top of, so a scope change,
  // fresh data or a discard drops them along with the edits.
  const [added, setAdded] = useState({ forRows: null, rows: NO_ADDED_ROWS });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  // How many columns have a filter set, for the Clear filters button.
  const [filteredColumnCount, setFilteredColumnCount] = useState(0);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const gridApiRef = useRef(null);
  const [editVersion, setEditVersion] = useState(0);

  // Only honour the URL's group once it is a known invoice group; anything else is the default
  // price list.
  const selectedGroup = invoiceGroups.find(
    ({ invoiceGroupId }) => invoiceGroupId === invoiceGroupIdParam
  );
  const invoiceGroupId = selectedGroup?.invoiceGroupId ?? '';
  const isLoadingOverrides = !!invoiceGroupId && loadedOverrides.groupId !== invoiceGroupId;

  useEffect(() => {
    document.title = 'Product Pricing - SmartOS';
  }, []);

  useEffect(() => {
    listInvoiceGroupsApi()
      .then((response) => {
        const groups = [...(response?.results ?? [])].sort((a, b) =>
          (a.invoiceGroupName || '').localeCompare(b.invoiceGroupName || '')
        );
        setInvoiceGroups(groups);
      })
      .catch(() => enqueueSnackbar('Failed to load invoice groups', { variant: 'error' }))
      .finally(() => setIsLoadingGroups(false));
  }, []);

  useEffect(() => {
    listProductPricingsApi()
      .then((response) => setDefaults(response?.results ?? []))
      .catch(() => {
        setIsDefaultsError(true);
        enqueueSnackbar('Failed to load product pricing', { variant: 'error' });
      })
      .finally(() => setIsLoadingDefaults(false));
  }, []);

  // The group can change while its overrides are still loading; only the latest request may
  // write state, so a slow response for a previous group never replaces the current one's.
  const latestOverridesRequest = useRef('');

  useEffect(() => {
    latestOverridesRequest.current = invoiceGroupId;

    if (!invoiceGroupId) return;

    listInvoiceGroupProductPricingsApi({ invoiceGroupId })
      .then((response) => ({ results: response?.results ?? [], isError: false }))
      .catch(() => {
        if (latestOverridesRequest.current === invoiceGroupId) {
          enqueueSnackbar('Failed to load invoice group pricing', { variant: 'error' });
        }
        return { results: [], isError: true };
      })
      .then(({ results, isError }) => {
        if (latestOverridesRequest.current === invoiceGroupId) {
          setLoadedOverrides({ groupId: invoiceGroupId, results, isError });
        }
      });
  }, [invoiceGroupId]);

  // The default price list, or only the selected invoice group's overrides. Fresh row objects
  // every time: AG Grid writes edits straight into rowData, so the API's results must never be
  // handed to it directly.
  const rows = useMemo(
    () => {
      if (!invoiceGroupId) return defaults.map((pricing) => ({ ...pricing }));
      if (isLoadingOverrides) return [];
      return loadedOverrides.results.map((pricing) => ({ ...pricing }));
    },
    // resetToken is a deliberate dependency: bumping it rebuilds the rows, discarding edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaults, loadedOverrides, invoiceGroupId, isLoadingOverrides, resetToken]
  );

  // What each row looked like when loaded, and which cells have been edited since (for the
  // unsaved-changes count). Both belong to one set of rows: new rows (a scope change, fresh data
  // or a discard) start with no edits.
  const { baselineByNdc, editedCells } = useMemo(
    () => ({
      baselineByNdc: new Map(rows.map((row) => [row.ndc, { ...row }])),
      editedCells: new Set(),
    }),
    [rows]
  );

  // Cells the last save was refused for (key -> message), cleared as each is edited and whenever
  // the rows are rebuilt (a successful save, a discard, a scope change).
  const [saveErrors, setSaveErrors] = useState(new Map());

  useEffect(() => {
    setSaveErrors((current) => (current.size === 0 ? current : new Map()));
  }, [rows]);

  const addedRows = added.forRows === rows ? added.rows : NO_ADDED_ROWS;
  const rowData = useMemo(() => [...rows, ...addedRows], [rows, addedRows]);
  // NDCs are unique: the add dialog won't take one that's already in the grid.
  const existingNdcs = useMemo(() => new Set(rowData.map(({ ndc }) => ndc)), [rowData]);
  // An invoice group can only override products that are in the default price list.
  const defaultNdcs = useMemo(() => new Set(defaults.map(({ ndc }) => ndc)), [defaults]);
  const addedNdcs = useMemo(() => new Set(addedRows.map(({ ndc }) => ndc)), [addedRows]);
  const unsavedCount = editedCells.size + addedRows.length;

  // Unsaved changes hold any in-app navigation: leaving the page, and switching "Pricing for",
  // which changes ?invoiceGroupId= (the picker only moves once the admin confirms).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      unsavedCount > 0 &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search)
  );

  useEffect(() => {
    if (blocker.state === 'blocked' && unsavedCount === 0) blocker.reset();
  }, [blocker, unsavedCount]);

  // Reloading, closing the tab or typing a URL can only be caught by the browser's own prompt.
  useEffect(() => {
    if (unsavedCount === 0) return undefined;

    const warnBeforeUnload = (event) => {
      event.preventDefault();
      // Required by some browsers to show the prompt; the text itself is not displayed.
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [unsavedCount]);

  // The NDC of a product just added, waiting for the grid to take in the new row so it can be
  // scrolled into view (see handleRowDataUpdated).
  const scrollToNdcRef = useRef(null);

  const handleAddRow = (pricing) => {
    scrollToNdcRef.current = pricing.ndc;
    setAdded({ forRows: rows, rows: [...addedRows, pricing] });
    setIsAddDialogOpen(false);
  };

  // Runs once AG Grid has applied new rowData. Scrolling straight after setState is too early:
  // the grid hasn't received the new row yet, so there's nothing to scroll to.
  const handleRowDataUpdated = useCallback(({ api }) => {
    const ndc = scrollToNdcRef.current;
    if (!ndc) return;

    const node = api.getRowNode(ndc);
    if (!node) return;
    scrollToNdcRef.current = null;

    // A column filter can hide the new row; there's nothing on screen to scroll to then.
    if (!node.displayed) {
      enqueueSnackbar(`Product ${ndc} was added but is hidden by the current filters`, {
        variant: 'info',
      });
      return;
    }

    // Wherever the current sort places it.
    api.ensureNodeVisible(node, 'middle');
  }, []);

  // Whether a cell differs from what was loaded. The highlight is worked out from the row's data
  // rather than from the edited set: AG Grid redraws an edited cell before onCellValueChanged
  // fires, so a highlight driven by that event would lag one edit behind.
  const isCellEdited = useCallback(
    (data, field) => {
      const baseline = baselineByNdc.get(data.ndc);
      return !!baseline && !isEqual(data[field] ?? null, baseline[field] ?? null);
    },
    [baselineByNdc]
  );

  // AG Grid's React wrapper keeps the onCellValueChanged it was first given (from before any
  // rows loaded), so the handler reads the current edit-tracking state through this ref rather
  // than closing over it.
  const editTrackingRef = useRef();
  editTrackingRef.current = { addedNdcs, editedCells, isCellEdited };

  const handleCellValueChanged = useCallback(({ data, colDef }) => {
    const tracking = editTrackingRef.current;

    // An edited cell the last save was refused for is the admin fixing it: stop flagging it.
    const errorKey = saveErrorKey(data.ndc, colDef.field);
    setSaveErrors((current) => {
      if (!current.has(errorKey)) return current;
      const next = new Map(current);
      next.delete(errorKey);
      return next;
    });

    // An added row is one unsaved change as a whole; its cells aren't tracked separately.
    if (tracking.addedNdcs.has(data.ndc)) return;

    const key = editedCellKey(data.ndc, colDef.field);

    // Editing a cell back to its loaded value clears it.
    if (tracking.isCellEdited(data, colDef.field)) {
      tracking.editedCells.add(key);
    } else {
      tracking.editedCells.delete(key);
    }

    // AG Grid redraws only the edited cell, and refreshCells doesn't re-run cellStyle for the
    // rest of the row; bumping editVersion gives cellStyle (and so the column defs) a new
    // identity, which does.
    setEditVersion((version) => version + 1);
  }, []);

  // A row goes into the next save if it was added or has an edited cell.
  const isPendingSave = useCallback(
    (row) => addedNdcs.has(row.ndc) || EDITABLE_FIELDS.some((field) => isCellEdited(row, field)),
    [addedNdcs, isCellEdited]
  );

  // Invalid cells (a required field left empty, a negative amount) in rows about to be saved.
  // They show red, and Save stays disabled until they're fixed. Untouched rows aren't checked:
  // some override rows load with blank descriptive fields, and they aren't being saved.
  const pendingRows = rowData.filter(isPendingSave);
  const hasInvalidCells = pendingRows.some((row) =>
    VALIDATED_FIELDS.some((field) => getCellProblem(row, field))
  );

  const cellStyle = useCallback(
    ({ data, colDef }) => {
      const isInvalid = !!getCellProblem(data, colDef.field) && isPendingSave(data);

      const hasError = isInvalid || saveErrors.has(saveErrorKey(data.ndc, colDef.field));

      let backgroundColor = 'transparent';
      if (hasError) {
        backgroundColor = ERROR_CELL_COLOR;
      } else if (addedNdcs.has(data.ndc)) backgroundColor = ADDED_ROW_COLOR;
      else if (isCellEdited(data, colDef.field)) backgroundColor = EDITED_CELL_COLOR;
      else if (EDITABLE_FIELDS.some((field) => isCellEdited(data, field))) {
        backgroundColor = EDITED_ROW_COLOR;
      }

      // AG Grid never removes a style it applied earlier, so every property is always set.
      return {
        backgroundColor,
        boxShadow: hasError ? ERROR_CELL_OUTLINE : 'none',
        textAlign: getTextAlign(colDef.field),
      };
    },
    // editVersion is deliberate: see handleCellValueChanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addedNdcs, isCellEdited, isPendingSave, saveErrors, editVersion]
  );

  const columnDefs = useMemo(() => buildColumnDefs(cellStyle), [cellStyle]);

  // Saves the added rows and every row with an edited cell, as full rows (the API replaces each
  // row it's sent). Blocking: nothing can be edited, added, discarded or switched until it ends.
  // On success the rows the API stored are folded back into the list, which rebuilds the grid's
  // rows in place (matched by NDC): highlights clear and the view stays where it was.
  const handleSave = async () => {
    const payload = pendingRows.map(toSavePayload);

    if (payload.length === 0 || hasInvalidCells) return;

    gridApiRef.current?.stopEditing();
    setIsSaving(true);

    try {
      const saved = invoiceGroupId
        ? await updateInvoiceGroupProductPricingsApi({ invoiceGroupId, productPricings: payload })
        : await updateProductPricingsApi({ productPricings: payload });
      const results = saved?.results ?? [];

      if (invoiceGroupId) {
        setLoadedOverrides((current) => ({
          ...current,
          results: mergeSavedRows(current.results, results),
        }));
      } else {
        setDefaults((current) => mergeSavedRows(current, results));
      }

      enqueueSnackbar(`Saved ${payload.length} ${payload.length === 1 ? 'product' : 'products'}`, {
        variant: 'success',
      });
    } catch (error) {
      // Nothing was written; the edits stay. A validation failure names the bad cells.
      const graphqlError = error?.response?.errors?.[0];
      const details = graphqlError?.extensions?.details;

      if (Array.isArray(details) && details.length > 0) {
        setSaveErrors(toSaveErrors(details, payload));
        enqueueSnackbar('Not saved: fix the highlighted cells and save again', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(graphqlError?.message || 'Failed to save pricing', { variant: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Downloads what the grid shows: the current filters and sort, unsaved edits included. Row
  // numbers are left out (AG Grid's default). Named for the pricing and the local date and time,
  // e.g. product-pricing-default-2026-10-05_14-32-07.csv (dashes, as Windows forbids ':' in
  // file names).
  const handleExportCsv = () => {
    const scope = (invoiceGroupId ? selectedGroup.invoiceGroupName : 'default')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    gridApiRef.current?.exportDataAsCsv({
      fileName: `product-pricing-${scope}-${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`,
    });
  };

  const handleGroupChange = (_, option) => {
    setSearchParams(option?.invoiceGroupId ? { invoiceGroupId: option.invoiceGroupId } : {});
  };

  // A deep link to a group can't be shown until the groups are known, or it would briefly
  // render the default price list under that URL.
  const isResolvingGroup = !!invoiceGroupIdParam && isLoadingGroups;
  const isLoading = isResolvingGroup || (invoiceGroupId ? isLoadingOverrides : isLoadingDefaults);
  const isError = invoiceGroupId ? loadedOverrides.isError : isDefaultsError;

  return (
    <Grid container direction='column' sx={{ px: 3, pb: 3 }} gap={2}>
      <Grid item container justifyContent='space-between' alignItems='center' rowGap={2}>
        <Grid item>
          <Typography component='h1' variant='h5'>
            Product Pricing
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {invoiceGroupId
              ? `Pricing overrides for ${selectedGroup.invoiceGroupName}. Products not listed are billed at the default price.`
              : 'Default prices billed to every invoice group without an override.'}
          </Typography>
        </Grid>
        <Stack direction='row' spacing={2} alignItems='center'>
          <Autocomplete
            size='small'
            disableClearable
            options={[DEFAULT_PRICING_OPTION, ...invoiceGroups]}
            value={selectedGroup ?? DEFAULT_PRICING_OPTION}
            onChange={handleGroupChange}
            disabled={isSaving}
            getOptionLabel={(option) =>
              option.invoiceGroupId && !option.isActive
                ? `${option.invoiceGroupName} (inactive)`
                : option.invoiceGroupName
            }
            isOptionEqualToValue={(option, value) => option.invoiceGroupId === value.invoiceGroupId}
            sx={{ minWidth: 280 }}
            renderInput={(params) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <TextField {...params} label='Pricing for' />
            )}
          />
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => setIsAddDialogOpen(true)}
            disabled={isLoading || isError || isSaving}
          >
            Add Product
          </Button>
          {/* The span lets the tooltip show over the button while it's disabled. */}
          <Tooltip title={hasInvalidCells ? 'Fix the fields highlighted in red' : ''}>
            <span>
              <LoadingButton
                variant='contained'
                startIcon={<SaveIcon />}
                onClick={handleSave}
                loading={isSaving}
                loadingPosition='start'
                disabled={unsavedCount === 0 || hasInvalidCells || isLoading || isError}
              >
                Save
              </LoadingButton>
            </span>
          </Tooltip>
        </Stack>
      </Grid>

      <SaveErrorsAlert errors={saveErrors} rows={rowData} />

      {isError ? (
        <Typography color='error'>
          {invoiceGroupId
            ? 'Unable to load pricing overrides for this invoice group.'
            : 'Unable to load product pricing.'}
        </Typography>
      ) : (
        <Box sx={{ height: 'calc(100vh - 350px)', minHeight: 400 }}>
          <AgGridReact
            onGridReady={({ api }) => {
              gridApiRef.current = api;
            }}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            theme={spreadsheetTheme}
            // Spreadsheet-style row numbers, following the current sort and filter.
            rowNumbers
            suppressMultiSort
            getRowId={({ data }) => data.ndc}
            loading={isLoading || isSaving}
            overlayNoRowsTemplate={
              invoiceGroupId
                ? '<span>This invoice group has no pricing overrides.</span>'
                : '<span>No product pricing found.</span>'
            }
            onCellValueChanged={handleCellValueChanged}
            onRowDataUpdated={handleRowDataUpdated}
            onFilterChanged={({ api }) =>
              setFilteredColumnCount(Object.keys(api.getFilterModel() ?? {}).length)
            }
            stopEditingWhenCellsLoseFocus
            singleClickEdit
            undoRedoCellEditing
          />
        </Box>
      )}

      <GridFooterActions
        canExport={!isLoading && !isError && !isSaving}
        onExport={handleExportCsv}
        filteredColumnCount={filteredColumnCount}
        onClearFilters={() => gridApiRef.current?.setFilterModel(null)}
        unsavedCount={unsavedCount}
        onDiscard={() => setIsDiscardConfirmOpen(true)}
        disabled={isSaving}
      />

      <LeaveWithoutSavingDialog
        open={blocker.state === 'blocked'}
        unsavedCount={unsavedCount}
        onStay={() => blocker.reset?.()}
        onLeave={() => blocker.proceed?.()}
      />

      <DiscardChangesDialog
        open={isDiscardConfirmOpen}
        unsavedCount={unsavedCount}
        onKeepEditing={() => setIsDiscardConfirmOpen(false)}
        onDiscard={() => {
          setResetToken((n) => n + 1);
          setIsDiscardConfirmOpen(false);
        }}
      />

      <AddProductPricingDialog
        open={isAddDialogOpen}
        existingNdcs={existingNdcs}
        allowedNdcs={invoiceGroupId ? defaultNdcs : null}
        onClose={() => setIsAddDialogOpen(false)}
        onAdd={handleAddRow}
      />
    </Grid>
  );
};

export default ProductPricing;
